import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "lifecycle-for-edge-network-migration-reference"
    }}>{`Lifecycle for Edge Network migration reference`}</h1>
    <p>{`The Lifecycle for Edge Network Mobile SDK extension enables collecting application lifecycle data including launches, installs, upgrades, and closes in addition to application and device dimensions such as the application identifier and device name and model. When used together with the Mobile SDK Edge Network extension, Lifecycle events are sent to the Adobe Experience Platform Edge Network on each launch and close of the application.`}</p>
    <p>{`The Lifecycle for Edge Network extension is built into the existing Lifecycle extension bundled with Mobile Core and introduces new events to capture when an application is launched and closed. These events are separate and distinct from the Lifecycle events handled by the Analytics extension and are dispatched from different triggers and contain different metrics and dimensions. Both "Lifecycle for Edge Network" and "Lifecycle for Analytics" serve similar functions but behave in different ways. The changes made to the Lifecycle extension to support the Edge Network were done, in part, to support AJO and CJA features and provide greater flexibility at reporting time. The following outlines these changes to give you an understanding of the changes to your data when migrating from Lifecycle for Analytics to Lifecycle for Edge Network.`}</p>
    <h2 {...{
      "id": "installing-lifecycle-for-edge-network"
    }}>{`Installing Lifecycle for Edge Network`}</h2>
    <p>{`The Mobile SDK Lifecycle for Edge Network extension is used together with the Mobile SDK Edge Network extension, and both must be included and registered in an application. A rule is required, however, to forward the Lifecycle events to the Edge Network extension. To configure and install Lifecycle for Edge Network, follow the instructions from the `}<a parentName="p" {...{
        "href": "/client-sdks/src/pages/edge/lifecycle-for-edge-network/index.md#configure-and-install-lifecycle-for-edge-network"
      }}>{`overview page`}</a>{` to set up your XDM schema, add a rule to forward the Lifecycle Application Launch and Application Close events to the Edge Network, add the Edge Network extension to your application, and add Lifecycle and the required API calls to your application if not already done. The Lifecycle for Edge Network does not add any new APIs so application changes to migrate to the Edge Network from Analytics are minimal. To disable Lifecycle for Analytics, simply remove the Analytics extension from your application.`}</p>
    <h2 {...{
      "id": "lifecycle-events-when-using-edge"
    }}>{`Lifecycle events when using Edge`}</h2>
    <p>{`Lifecycle for Edge Network introduces two new events to the Mobile SDK, `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/lifecycle/event-reference.md#lifecycle-application-foreground"
      }}>{`Application Launch (Foreground)`}</a>{` and `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/lifecycle/event-reference.md#lifecycle-application-background"
      }}>{`Application Close (Background)`}</a>{`. These events are dispatched each time an application is brought to the foreground and sent to the background respectively. This is a significant change from Lifecycle for Analytics which kept track of an internal session based on a configurable session timeout. This change will introduce more Lifecycle events to your reports, however knowing when a user launches and closes your application allows for more accurate user journeys.`}</p>
    <p>{`The session timeout configuration set in the Mobile Core card in Tags is not used in Lifecycle for Edge Network. There is no concept of a session or how long a user must background the application before a new Lifecycle event is dispatched. Instead, Lifecycle events are simply dispatched when the application is launched and when the application is closed.`}</p>
    <p>{`To understand this difference, take the following example. An application is using both Lifecycle for Edge Network and Lifecycle for Analytics. Mobile Core is configured with a session timeout of 300 seconds (5 minutes). The following chart displays when and how many events are dispatched from each path.`}</p>
    <h3 {...{
      "id": "lifecycle-events-edge-network-vs-analytics"
    }}>{`Lifecycle events Edge Network vs Analytics`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}><strong parentName="th">{`Application state`}</strong></th>
          <th parentName="tr" {...{
            "align": "left"
          }}><strong parentName="th">{`Edge Network event`}</strong></th>
          <th parentName="tr" {...{
            "align": "left"
          }}><strong parentName="th">{`Analytics event`}</strong></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`app launched`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Application Launch (Foreground)`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Lifecycle Start and SessionInfo*`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`app sent to background`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Application Close (Background)`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`no event, session paused`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`app launched after 3 minutes`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Application Launch (Foreground)`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`no event, session resumed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`app sent to background`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Application Close (Background)`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`no event, session paused`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`app launched after 30 minutes`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Application Launch (Foreground)`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Lifecycle Start and SessionInfo*`}</td>
        </tr>
      </tbody>
    </table>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`When using Lifecycle for Analytics, if the Analytics extension in Tags is configured with "backdate previous session info" and "offline enabled", then two events are dispatched on the start of a new session; one for the previous session info and one for the start of the new Lifecycle session. If these configurations are not set, then only one event is dispatched for the start of the new Lifecycle session.`}</p>
    <p>{`As you can see in the previous scenario, Lifecycle for Edge Network provides richer details on how a user interacts with an application. This level of granularity comes at a cost of more events being sent to the Edge Network than was previously sent when using Analytic directly.`}</p>
    <h2 {...{
      "id": "lifecycle-data"
    }}>{`Lifecycle data`}</h2>
    <p>{`Lifecycle for Edge Network formats data to XDM, conforming to the `}<a parentName="p" {...{
        "href": "https://github.com/adobe/xdm/blob/master/docs/reference/adobe/experience/aep-mobile-lifecycle-details.schema.md"
      }}>{`AEP Mobile Lifecycle Details`}</a>{` field group. The field group contains three subgroups: application details which are the Lifecycle data, environment details about the system software, and device details about the user's device. This Lifecycle data sent to the Edge Network is different in a couple key ways from the data sent from the mobile Analytics extension.`}</p>
    <ol>
      <li parentName="ol">{`Any metric or dimension which may be computed at reporting time from existing data is not included in Lifecycle for Edge Network events. This includes metrics such as `}<strong parentName="li">{`launch number`}</strong>{`, `}<strong parentName="li">{`launches since upgrade`}</strong>{`, `}<strong parentName="li">{`days since first use`}</strong>{`, `}<strong parentName="li">{`days since last use`}</strong>{`, `}<strong parentName="li">{`days since last upgrade`}</strong>{`, and dimensions such as events for `}<strong parentName="li">{`daily or monthly engaged user`}</strong>{`, `}<strong parentName="li">{`hour of day`}</strong>{` or `}<strong parentName="li">{`day of week`}</strong>{`, and `}<strong parentName="li">{`install date`}</strong>{`.`}</li>
      <li parentName="ol">{`The Edge Network extension handles more than just tracking events, as such Lifecycle dimensions are not sent on non-Lifecycle experience events as they are when using the mobile Analytics extension. These dimensions include `}<strong parentName="li">{`operating system`}</strong>{`, `}<strong parentName="li">{`device name and resolution`}</strong>{`, `}<strong parentName="li">{`carrier name`}</strong>{`, `}<strong parentName="li">{`run mode`}</strong>{`, and the `}<strong parentName="li">{`application identifier`}</strong>{`. If necessary, application developers may add these dimensions to specific Edge Network requests either manually when calling `}<a parentName="li" {...{
          "href": "../edge-network/api-reference.md#sendevent"
        }}>{`Edge.sendEvent()`}</a>{` or by configuring an attach data rule in Tags.`}</li>
    </ol>
    <p>{`Refer to the Lifecycle data reference documentation for a comparison between the `}<a parentName="p" {...{
        "href": "./metrics.md"
      }}>{`Lifecycle for Edge Network data`}</a>{` and `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/lifecycle/metrics.md"
      }}>{`Lifecycle for Analytics data`}</a>{`.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The Adobe Experience Platform Edge Network automatically maps the Lifecycle XDM variables into Adobe Analytics with no additional configuration needed. When enabling the Analytic service in a datastream, the XDM Lifecycle variables for application, device, and environment are automatically mapped as defined in `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/analytics/implementation/aep-edge/xdm-var-mapping"
      }}>{`XDM object variable mapping`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      